import {
	isUserLogged,
	useIsDepartmentDirector,
	useIsDepartmentSecretary,
	useIsFacultyAdmin,
	useIsProfessor,
	useIsStudent,
} from "../../utils/utilFunctions";
import { useQuery, useQueryClient } from "react-query";
import { PropsWithChildren, useState } from "react";
import {
	deleteProfessorInternship,
	updateProfessorInternshipViewCountByInternshipId,
	updateValidation,
} from "../../Requests/professor-internship-requests";
import {
	acceptCoordination,
	approveInternship,
	deleteCompanyInternship,
	markInternshipAsComplete,
	rejectCoordination,
	rejectInternship,
	updateCompanyInternshipViewCountByInternshipId,
	updateFacultyCoordinator,
} from "../../Requests/company-internship-requests";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../Notifications/NotificationsUtils";
import { useTranslation } from "react-i18next";
import { enrollStudentToInternship } from "../../Requests/internship-requests";
import { InternshipDTO, InternshipType } from "../../Api";
import { getAvailableStudents } from "../../Requests/academic-user-requests";
import {
	getAllApplicationTypesCount,
	getBachelorStudents,
	getCompanyInternships,
	getCoordinatedInternships,
	getDepartmentInternships,
	getInternshipsFilters,
	getMasterStudents,
	getProfessorInternships,
	getStudyProgramInternships,
	updateCompanyInternshipViewCount,
	updateProfessorInternshipViewCount,
} from "../../utils/reactQueriesConstants";
import { EventListProps } from "../../PropsLists/EventListProps";

export const useEventListController = (
	props: PropsWithChildren<EventListProps>
) => {
	const { t } = useTranslation();
	const isProfessor = useIsProfessor();
	const isStudent = useIsStudent();
	const isFacultyAdmin = useIsFacultyAdmin();
	const isDepartmentDirector = useIsDepartmentDirector();
	const isDepartmentSecretary = useIsDepartmentSecretary();
	const queryClient = useQueryClient();
	const [selectedInternshipId, setSelectedInternshipId] = useState<string>("");
	const [markCompleteModalVisibility, setMarkCompleteModalVisibility] =
		useState(false);
	const [deleteModalVisibility, setDeleteModalVisibility] = useState(false);
	const [approveModalVisibility, setApproveModalVisibility] = useState(false);
	const [rejectModalVisibility, setRejectModalVisibility] = useState(false);
	const [
		acceptCoordinationModalVisibility,
		setAcceptCoordinationModalVisibility,
	] = useState(false);
	const [
		rejectCoordinationModalVisibility,
		setRejectCoordinationModalVisibility,
	] = useState(false);
	const [spinning, setSpinning] = useState(false);

	const openErrorNotification = (_error: any) => {
		openNotification(
			t("internships.error"),
			t("internships.fetchDatasError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const updateViewCount = (internshipId: string, internshipType: string) => {
		if (isStudent && internshipType === InternshipType.ByCompany) {
			updateCompanyInternshipViewCountByInternshipId(internshipId)
				.then(async () => {
					await queryClient.invalidateQueries(updateCompanyInternshipViewCount);
				})
				.catch((_error) => {
					openErrorNotification(_error);
				});
		}

		if (isStudent && internshipType === InternshipType.ByProfessor) {
			updateProfessorInternshipViewCountByInternshipId(internshipId)
				.then(async () => {
					await queryClient.invalidateQueries(
						updateProfessorInternshipViewCount
					);
				})
				.catch((_error) => {
					openErrorNotification(_error);
				});
		}
	};

	const invalidateInternshipsQueries = async () => {
		await queryClient.invalidateQueries(getInternshipsFilters);
		await queryClient.invalidateQueries(getCompanyInternships);
		await queryClient.invalidateQueries(getProfessorInternships);
		await queryClient.invalidateQueries(getCoordinatedInternships);
		await queryClient.invalidateQueries(getAllApplicationTypesCount);
	};

	const deleteEntry = () => {
		setSpinning(true);

		(isUserLogged() && isProfessor
			? deleteProfessorInternship(selectedInternshipId)
			: deleteCompanyInternship(selectedInternshipId)
		)
			.then(async () => {
				await invalidateInternshipsQueries();
				openNotification(
					t("internships.deleteInternship"),
					t("internships.deleteInternshipSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					t("internships.deleteInternship"),
					t("internships.deleteInternshipError"),
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
				setDeleteModalVisibility(false);
			});
	};

	const sendForApproval = (trusted: boolean) => {
		setSpinning(true);

		markInternshipAsComplete(selectedInternshipId)
			.then(async () => {
				await invalidateInternshipsQueries();
				openNotification(
					trusted ? t("internships.finalize") : t("internships.markComplete"),
					trusted
						? t("internships.finalizeSuccess")
						: t("internships.completeInternshipSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					trusted ? t("internships.finalize") : t("internships.markComplete"),
					trusted
						? t("internships.finalizeError")
						: t("internships.completeInternshipError"),
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
				setMarkCompleteModalVisibility(false);
			});
	};

	const approve = () => {
		setSpinning(true);

		approveInternship(selectedInternshipId)
			.then(async () => {
				await invalidateInternshipsQueries();
				openNotification(
					t("internships.approveInternship"),
					t("internships.approveInternshipSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					t("internships.approveInternship"),
					t("internships.approveInternshipError"),
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
				setApproveModalVisibility(false);
			});
	};

	const reject = (item: any, description: string) => {
		setSpinning(true);

		rejectInternship(item.id, { message: description })
			.then(async () => {
				await invalidateInternshipsQueries();
				openNotification(
					t("internships.rejectInternship"),
					t("internships.rejectInternshipSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					t("internships.rejectInternship"),
					t("internships.rejectInternshipError"),
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
				setRejectModalVisibility(false);
			});
	};

	const acceptInternshipCoordination = () => {
		setSpinning(true);

		acceptCoordination(selectedInternshipId)
			.then(async () => {
				await invalidateInternshipsQueries();
				openNotification(
					t("internships.acceptCoordination"),
					t("internships.acceptCoordinationSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					t("internships.acceptCoordination"),
					t("internships.acceptCoordinationError"),
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
				setAcceptCoordinationModalVisibility(false);
			});
	};

	const rejectInternshipCoordination = () => {
		setSpinning(true);

		rejectCoordination(selectedInternshipId)
			.then(async () => {
				await invalidateInternshipsQueries();
				openNotification(
					t("internships.rejectCoordination"),
					t("internships.rejectCoordinationSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					t("internships.rejectCoordination"),
					t("internships.rejectCoordinationError"),
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
				setRejectCoordinationModalVisibility(false);
			});
	};

	const updateInternshipCoordinator = (
		coordinatorId: any,
		internshipId: any
	) => {
		const bodyReq = {
			facultyCoordinatorId: coordinatorId,
		};
		updateFacultyCoordinator(internshipId, bodyReq)
			.then(async () => {
				await queryClient.invalidateQueries(getCompanyInternships);
				await queryClient.invalidateQueries(getCoordinatedInternships);
				await queryClient.invalidateQueries(getInternshipsFilters);
				openNotification(
					t("admin.updateInternshipResponsible"),
					isProfessor && !isFacultyAdmin
						? t("admin.successAutoUpdatedInternshipResponsible")
						: t("admin.successUpdatedInternshipResponsible"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					t("admin.updateInternshipResponsible"),
					isProfessor && !isFacultyAdmin
						? t("admin.errorAutoUpdatedInternshipResponsible")
						: t("admin.errorUpdatedInternshipResponsible"),
					NOTIFICATION_TYPES.ERROR
				);
			});
	};

	const enrollStudent = (studentId: any, internshipId: any) => {
		enrollStudentToInternship(studentId, internshipId)
			.then(async () => {
				await queryClient.invalidateQueries(getProfessorInternships);
				await queryClient.invalidateQueries(getBachelorStudents);
				await queryClient.invalidateQueries(getMasterStudents);
				await queryClient.invalidateQueries(getAllApplicationTypesCount);
				openNotification(
					t("admin.enrollStudentMessage"),
					t("admin.successEnrollStudent"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					t("admin.enrollStudentMessage"),
					t("admin.errorEnrollStudent"),
					NOTIFICATION_TYPES.ERROR
				);
			});
	};

	const onChangeValidation = (checked: boolean, item: InternshipDTO) => {
		updateValidation(item.id, checked)
			.then(async () => {
				if (isDepartmentDirector) {
					await queryClient.invalidateQueries(getDepartmentInternships);
				} else {
					await queryClient.invalidateQueries(getStudyProgramInternships);
				}
			})
			.catch(() => {
				if (checked) {
					openNotification(
						t("internships.approveInternship"),
						t("internships.approveInternshipError"),
						NOTIFICATION_TYPES.ERROR
					);
				} else {
					openNotification(
						t("internships.rejectInternship"),
						t("internships.rejectInternshipError"),
						NOTIFICATION_TYPES.ERROR
					);
				}
			});
	};

	return {
		state: {
			selectedInternshipId,
			markCompleteModalVisibility,
			deleteModalVisibility,
			approveModalVisibility,
			rejectModalVisibility,
			acceptCoordinationModalVisibility,
			rejectCoordinationModalVisibility,
			spinning,
		},
		actions: {
			updateViewCount,
			deleteEntry,
			sendForApproval,
			approve,
			reject,
			acceptInternshipCoordination,
			rejectInternshipCoordination,
			updateInternshipCoordinator,
			enrollStudent,
			onChangeValidation,
			setSelectedInternshipId,
			setMarkCompleteModalVisibility,
			setDeleteModalVisibility,
			setApproveModalVisibility,
			setAcceptCoordinationModalVisibility,
			setRejectCoordinationModalVisibility,
		},
	};
};
